import React, { useEffect, useState } from 'react'
import { SeeAllContainer, PaginationControls } from '@islandsstofa/react'
import useGetAll from '@root/src/hooks/useGetAll'
import { Opportunity, Capital, Event } from '@root/src/utils/resolvers'
import { OpportunityCard } from '@root/src/components/site/Cards'
import DropdownTree, {
  DropdownType,
  BooleanObjectType
} from '@cmp/site/DropdownTree'

type CardType = Opportunity | Capital | Event

const MAX_RESULTS = 9

const Results = ({
  visibleTags = {},
  categories = {},
  cards,
  title = ''
}: {
  visibleTags: DropdownType
  categories: BooleanObjectType
  cards: CardType[]
  title?: string
}) => {
  const [filteredCards, setFilteredCards] = useState<CardType[]>(cards)
  const [page, setPage] = useState(0)
  useEffect(() => {
    setFilteredCards(
      cards
        // Select all checked categories
        .filter(
          card =>
            (card.category && categories[card.category]) ||
            Object.values(categories).every(cat => !cat)
        )
        // Then filter by tag based on selected categories
        .filter(card => {
          let tags: any = []
          if (card.category && visibleTags[card.category]) {
            tags = visibleTags[card.category]
          }
          return (
            Object.values(tags).every(cat => !cat) ||
            card.customTags.filter(tag => tags[tag]).length > 0
          )
        })
    )
  }, [cards])
  useEffect(() => {
    setPage(0)
  }, [visibleTags, categories])

  if (!filteredCards) return null
  const pages =
    filteredCards.length > 0
      ? Math.floor(filteredCards.length / MAX_RESULTS + 0.9)
      : 1
  return (
    <div className='see-all__results'>
      <h3>{title}</h3>
      <div className='container page-slice-spacer p-0'>
        <div className='card-grid'>
          {filteredCards.length > 0 &&
            filteredCards
              .slice(page * MAX_RESULTS, page * MAX_RESULTS + MAX_RESULTS)
              .map((card, idx) => (
                <OpportunityCard
                  key={idx}
                  className='card--tall position-relative'
                  wrapperClassName='card--gap card--opportunity position-relative'
                  card={{ ...card, tags: card.customTags }}
                  excerptMaxLen={card.customTags[0] ? 90 : 150}
                />
              ))}
        </div>
      </div>
      {filteredCards.length > MAX_RESULTS && (
        <PaginationControls
          max={pages}
          next={() => setPage(page + 1)}
          prev={() => setPage(page - 1)}
          page={page}
          goToPage={idx => setPage(idx)}
        />
      )}
    </div>
  )
}

export default () => {
  const [visibleTags, setVisibleTags] = useState<DropdownType>({})
  const [categories, setCategories] = useState<BooleanObjectType>({})
  const baseCategories: { [key: string]: string[] } = {
    Tækifæri: ['Styrkur', 'Atvinnutækifæri', 'Leit að samstarfsaðila'],
    Fjármögnun: ['Styrkur', 'Lán', 'Hlutafé', 'Innkaup']
  }
  const tagExists = (cards: CardType[], category: string, tag: string) => {
    return cards
      .filter(card => card.category === category)
      .map(card => card.customTags)
      .flat(1)
      .includes(tag)
  }

  const cards = useGetAll(true).map(card => ({
    ...card, // Sort by most relevant tag
    customTags: card.customTags.sort((a, b) => {
      if (
        card.customTags.indexOf(a) === 0 ||
        card.customTags.indexOf(b) === 0
      ) {
        return 1
      }
      if (
        a &&
        card.category &&
        baseCategories[card.category] &&
        baseCategories[card.category].includes(a.toLowerCase())
      ) {
        return -1
      }
      if (
        b &&
        card.category &&
        baseCategories[card.category] &&
        baseCategories[card.category].includes(b.toLowerCase())
      ) {
        return 1
      }
      return 0
    })
  }))

  useEffect(() => {
    setVisibleTags({
      Tækifæri: baseCategories['Tækifæri']
        .filter(tag => tagExists(cards, 'Tækifæri', tag))
        .reduce((obj, item) => Object.assign(obj, { [item]: false }), {}),
      Fjármögnun: baseCategories['Fjármögnun']
        .filter(tag => tagExists(cards, 'Fjármögnun', tag))
        .reduce((obj, item) => Object.assign(obj, { [item]: false }), {})
    })

    let categoryObj: BooleanObjectType = {}
    const uniqueCategories = Array.from(
      new Set(cards.map(card => card.category || ''))
    )
    uniqueCategories.forEach(c => {
      categoryObj[c] = false
    })
    setCategories(categoryObj)
  }, [])

  return (
    <SeeAllContainer>
      <DropdownTree
        dropdowns={visibleTags}
        categories={categories}
        onDropdownUpdate={setVisibleTags}
        onSelectAll={setCategories}
      />
      <Results
        visibleTags={visibleTags}
        categories={categories}
        cards={cards}
      />
    </SeeAllContainer>
  )
}
